import React, { useEffect, useRef } from "react";

/**
 * Lightweight canvas particle field.
 * - Low density, accent-tinted, gentle drift
 * - Optional cursor influence (particles ease toward/away from pointer)
 * Props:
 *   density: 0..1 (multiplier on particle count)
 *   accent: hex color string
 *   influence: "pull" | "push" | "none"
 *   className
 */
function buildParticles(rect, density) {
    const target = Math.min(
        240,
        Math.max(40, Math.floor((rect.width * rect.height) / 14000) * density),
    );
    return new Array(Math.floor(target)).fill(0).map(() => ({
        x: Math.random() * rect.width,
        y: Math.random() * rect.height,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        r: Math.random() * 1.4 + 0.4,
        a: Math.random() * 0.5 + 0.15,
    }));
}

function stepParticle(p, state, influence) {
    if (influence !== "none") {
        const dx = state.mouse.x - p.x;
        const dy = state.mouse.y - p.y;
        const dist2 = dx * dx + dy * dy;
        if (dist2 < 160 * 160) {
            const force = (1 - Math.sqrt(dist2) / 160) * 0.08;
            const sign = influence === "pull" ? 1 : -1;
            p.vx += sign * (dx / 160) * force;
            p.vy += sign * (dy / 160) * force;
        }
    }
    p.x += p.vx;
    p.y += p.vy;
    p.vx *= 0.985;
    p.vy *= 0.985;
    p.vx += (Math.random() - 0.5) * 0.01;
    p.vy += (Math.random() - 0.5) * 0.01;
    if (p.x < -2) p.x = state.w + 2;
    if (p.x > state.w + 2) p.x = -2;
    if (p.y < -2) p.y = state.h + 2;
    if (p.y > state.h + 2) p.y = -2;
}

function drawParticle(ctx, p, accent) {
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    ctx.fillStyle = withAlpha(accent, p.a);
    ctx.shadowColor = withAlpha(accent, 0.7);
    ctx.shadowBlur = 6;
    ctx.fill();
}

export function ParticleField({
    density = 0.6,
    accent = "#D8FF3D",
    influence = "pull",
    className = "",
    drawHeadline = false,
}) {
    const canvasRef = useRef(null);
    const rafRef = useRef(0);
    const stateRef = useRef({ particles: [], mouse: { x: -9999, y: -9999 }, w: 0, h: 0, dpr: 1 });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const state = stateRef.current;

        function resize() {
            const rect = canvas.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            state.w = rect.width;
            state.h = rect.height;
            state.dpr = dpr;
            canvas.width = Math.floor(rect.width * dpr);
            canvas.height = Math.floor(rect.height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            // Build particles based on area & density
            const target = Math.min(
                240,
                Math.max(40, Math.floor((rect.width * rect.height) / 14000) * density),
            );
            state.particles = new Array(Math.floor(target)).fill(0).map(() => ({
                x: Math.random() * rect.width,
                y: Math.random() * rect.height,
                vx: (Math.random() - 0.5) * 0.18,
                vy: (Math.random() - 0.5) * 0.18,
                r: Math.random() * 1.4 + 0.4,
                a: Math.random() * 0.5 + 0.15,
            }));
        }

        function onMove(e) {
            const rect = canvas.getBoundingClientRect();
            state.mouse.x = e.clientX - rect.left;
            state.mouse.y = e.clientY - rect.top;
        }
        function onLeave() {
            state.mouse.x = -9999;
            state.mouse.y = -9999;
        }

        function tick() {
            ctx.clearRect(0, 0, state.w, state.h);
            for (const p of state.particles) {
                stepParticle(p, state, influence);
                drawParticle(ctx, p, accent);
            }
            rafRef.current = requestAnimationFrame(tick);
        }

        resize();
        window.addEventListener("resize", resize);
        window.addEventListener("mousemove", onMove);
        canvas.addEventListener("mouseleave", onLeave);
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener("resize", resize);
            window.removeEventListener("mousemove", onMove);
            canvas.removeEventListener("mouseleave", onLeave);
        };
        // canvas/ctx/state come from stable refs; handlers are created inside the effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [density, accent, influence, drawHeadline]);

    return (
        <canvas
            ref={canvasRef}
            className={`block w-full h-full ${className}`}
            style={{ willChange: "transform" }}
        />
    );
}

function withAlpha(hex, a) {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}
